import {noop} from '@benchling/js-utils';
import React from 'react';

import AlignmentRowInfo from 'analyses/alignment/AlignmentRowInfo';
import PolymerAlignmentRowView, {
  PolymerAlignmentRowViewInitializationOptions,
} from 'analyses/alignment/PolymerAlignmentRowView';
import {AlignmentRowProps} from 'analyses/AlignmentRow';
import BenchlingConfig from 'BenchlingConfig';
import * as vo from 'components/VisibilityOptions';
import {VisibilityOptions} from 'components/VisibilityOptions';
import {ProteinAlignmentKind, ProteinAlignmentRow} from 'constants/AlignmentConstants';
import {listenToAntibodyVisibilitySettings} from 'molbio/antibody-engineering/visibilityHelpers';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import Range from 'molbio/maps/selectables/Range';
import {Selection} from 'molbio/maps/selectables/Selection';
import {makeSliceView} from 'molbio/proteins/alignments/ProteinAlignmentUtil';
import * as ProteinProperties from 'molbio/proteins/util/ProteinProperties';
import * as sequtil from 'molbio/SeqUtil';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as bclip from 'util/BenchlingClipboard';

import ProteinAlignmentTabState from './ProteinAlignmentTabState';

interface ProteinAlignmentRowViewInitOptions
  extends PolymerAlignmentRowViewInitializationOptions<ProteinAlignmentTabState, ProteinAlignmentTabState> {
  alignmentType?: ProteinAlignmentKind;
  row: ProteinAlignmentRow;
  visibilityOptions: VisibilityOptions;
}

export default class ProteinAlignmentRowView extends PolymerAlignmentRowView<
  ProteinAlignmentTabState,
  ProteinAlignmentTabState
> {
  alignmentRowType!: ProteinAlignmentKind | null;
  mapstate = null; // for protein alignments, there is no mapstate model, just the tabstate
  row!: ProteinAlignmentRow;
  tabstate!: ProteinAlignmentTabState;

  constructor(options: ProteinAlignmentRowViewInitOptions) {
    super(options);
  }

  initialize(options: ProteinAlignmentRowViewInitOptions): void {
    super.initialize(options);
    this.row = options.row;
    this.alignmentRowType = options.alignmentType || null; // null for every row except the top row.
    this.tabstate = this.model; // for protein alignments, there is no mapstate model, just the tabstate
    if (BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS) {
      listenToAntibodyVisibilitySettings(this.visibilityOptions, this, this.renderNameInfo.bind(this));
    }
    this.listenTo(
      this.visibilityOptions.getOption(vo.ALIGNMENT_ROW_STATS.name),
      'change:visible',
      this.renderNameInfo.bind(this)
    );
  }

  getRange(): Range {
    return this.model.get('range');
  }

  getAlignmentRowType(): ProteinAlignmentKind | null {
    return this.alignmentRowType;
  }

  getRowContentProps(): AlignmentRowProps {
    return {
      alignmentRowType: this.getAlignmentRowType() ?? undefined,
      canEdit: false,
      options: this.visibilityOptions,
      // Protein alignments are read-only, so this count cannot update unless the entire alignment updates.
      featureCounts: {annotations: this.row.proteinVersion.annotations.length},
    };
  }

  getSlice(): Slice {
    return this.model.get('slice');
  }

  makeSliceView(): SliceView {
    return makeSliceView({
      alignmentRowType: this.alignmentRowType,
      tabState: this.tabstate,
      slice: this.tabstate.get('slice'),
      visibilityOptions: this.visibilityOptions,
      row: this.row,
      forAlignmentPNGExport: false,
    });
  }

  getSelection(): Selection {
    return this.model.get('selection');
  }

  getResidueSlice(start: number, end?: number): string {
    return sequtil.residueSlice(this.row.proteinVersion.aminoAcids, start, end, false);
  }

  handlePasteText(text: string): void {
    throw new Error('Unexpected call to handlePasteText: protein alignments are read-only');
  }

  handleCtrlShiftC(e: Event): void {
    // Proteins don't have a special copy menu. We prevent default behavior so the
    // element-select browser behavior in chrome doesn't trigger.
    // TODO: Code re-use with ProteinMapView
    e.preventDefault();
  }

  isValidCharacter(char: string): boolean {
    // TODO: Code re-use with ProteinMapView
    return ProteinProperties.areAminoAcidsValid(char);
  }

  showInputBubble(options?: unknown): void {
    throw new Error('Unexpected call to showInputBubble: protein alignments are read-only');
  }

  storeRegionInBenchlingClipboard(e: JQueryEventObject): string {
    const selection = this.getSelection();
    const aminoAcids = this.getResidueSlice(selection.start(), selection.end() ?? undefined);
    bclip.clipboard.store(aminoAcids);
    const clipboardDesc = `${this.row.proteinVersion.name}: ${selection.start()}-${selection.end()}aa`;
    bclip.clipboard.store(aminoAcids);
    return bclip.clipboard.getHash(aminoAcids, clipboardDesc);
  }

  renderNameInfo(): void {
    const alignment = this.model.get('alignment')!;
    const alignmentType = alignment.alignmentType;
    const templateResidues =
      alignmentType === ProteinAlignmentKind.TEMPLATE
        ? alignment.rows[0].proteinVersion.aminoAcids
        : undefined;
    renderToDOM(
      <AlignmentRowInfo
        residues={this.row.proteinVersion.aminoAcids}
        visibilityOptions={this.visibilityOptions}
        name={this.row.name}
        canEdit={false}
        isProteinAlignment={true}
        alignmentType={alignmentType}
        isReferenceRow={Boolean(this.getAlignmentRowType())}
        templateResidues={templateResidues}
      />,
      this.$el.find('.alignment-row-info-section')[0]
    );
  }

  handleContextMenu(e: JQueryMouseEventObject): void {
    // Protein alignments are read-only so don't show the context menu.
    e.preventDefault();
  }

  render(): this {
    super.render();
    this.renderNameInfo();
    return this;
  }

  undo(): void {
    // Protein alignments are read-only.
    noop();
  }
}

ProteinAlignmentRowView.initClass();
